import { useState } from "react";

import type { CategoriaProjecaoResultado, ValorMensal } from "@/modules/financeiro-obras";
import { NO_VALUE } from "../../planejamento-controle.constants";
import {
  formatCompetencia,
  formatMoeda,
  formatReaisSimples,
} from "../../planejamento-controle.format";
import {
  COLETOR_PENDENTE,
  INICIO_MARCA,
  SEM_DISTRIBUICAO,
  TOTAL_GERAL,
} from "./projecao-coletores-tabela.constants";
import type {
  CategoriaTabela,
  LinhaTabela,
  ProjecaoColetoresTabelaProps,
} from "./types-projecao-coletores-tabela";

const TITULOS: Record<CategoriaTabela, string> = { MATERIAIS: "Materiais", SERVICOS: "Serviços" };

const valorDe = (valores: ValorMensal[], competencia: string) =>
  valores.find((v) => v.competencia === competencia)?.valor;

const mes = (valores: ValorMensal[], competencia: string) => {
  const valor = valorDe(valores, competencia);
  return valor === undefined ? NO_VALUE : formatReaisSimples(valor);
};

function totalPorMes(categorias: CategoriaProjecaoResultado[], competencia: string): string {
  const valores = categorias
    .map((c) => valorDe(c.mensalizacao, competencia))
    .filter((v): v is number => v !== undefined);
  return valores.length === 0 ? NO_VALUE : formatReaisSimples(valores.reduce((a, b) => a + b, 0));
}

export function useProjecaoColetoresTabela({
  simulacao,
  distribuicoes,
  avisos,
  erro,
  calculando,
}: ProjecaoColetoresTabelaProps) {
  const [recolhidas, setRecolhidas] = useState<ReadonlySet<CategoriaTabela>>(new Set());

  const alternarCategoria = (categoria: CategoriaTabela) =>
    setRecolhidas((atual) => {
      const proximo = new Set(atual);
      if (!proximo.delete(categoria)) proximo.add(categoria);
      return proximo;
    });

  const competencias = simulacao?.competencias ?? [];
  const colunas = competencias.map((competencia) =>
    competencia === simulacao?.inicioMensalizacao
      ? `${formatCompetencia(competencia)} · ${INICIO_MARCA}`
      : formatCompetencia(competencia),
  );

  const linhas: LinhaTabela[] = [];
  if (simulacao) {
    const categorias: [CategoriaTabela, CategoriaProjecaoResultado | null][] = [
      ["MATERIAIS", simulacao.materiais],
      ["SERVICOS", simulacao.servicos],
    ];
    for (const [categoria, resultado] of categorias) {
      if (!resultado) continue;
      const expandida = !recolhidas.has(categoria);
      const pendente = resultado.pendenteCotacao;
      const emMoedaOriginal =
        pendente && resultado.valorBase !== null && resultado.moeda
          ? formatMoeda(resultado.valorBase, resultado.moeda)
          : null;
      linhas.push({
        key: categoria,
        testId: `projecao-linha-${categoria}`,
        nivel: "categoria",
        nome: `${TITULOS[categoria]} · ${distribuicoes[categoria] ?? SEM_DISTRIBUICAO}`,
        total: emMoedaOriginal ?? formatReaisSimples(resultado.valorReais),
        meses: competencias.map((c) => mes(resultado.mensalizacao, c)),
        expansivel: pendente || resultado.coletores.length > 0,
        expandida,
        categoria,
      });
      if (!expandida) continue;
      if (pendente) {
        linhas.push({
          key: `${categoria}-pendente`,
          testId: `projecao-linha-${categoria}-pendente`,
          nivel: "coletor",
          nome: COLETOR_PENDENTE,
          total: NO_VALUE,
          meses: competencias.map(() => NO_VALUE),
          expansivel: false,
          expandida: false,
          categoria,
        });
      }
      for (const coletor of resultado.coletores) {
        linhas.push({
          key: `${categoria}-${coletor.coletorId}`,
          testId: `projecao-linha-${categoria}-${coletor.coletorId}`,
          nivel: "coletor",
          nome: `${coletor.codigo} - ${coletor.nome}`,
          total: formatReaisSimples(coletor.valor),
          meses: competencias.map((c) => mes(coletor.valores, c)),
          expansivel: false,
          expandida: false,
          categoria,
        });
      }
    }
    const resultados = [simulacao.servicos, simulacao.materiais].filter(
      (c): c is CategoriaProjecaoResultado => c !== null,
    );
    linhas.push({
      key: "total",
      testId: "projecao-linha-total",
      nivel: "total",
      nome: TOTAL_GERAL,
      total: formatReaisSimples(simulacao.total),
      meses: competencias.map((c) => totalPorMes(resultados, c)),
      expansivel: false,
      expandida: false,
      categoria: null,
    });
  }

  return {
    colunas,
    linhas,
    avisos,
    erro,
    calculando,
    temTabela: simulacao !== null,
    alternarCategoria,
  };
}
